import jwtDecode from 'jwt-decode';
import { defineStore } from 'pinia';
import { EVENTS, logEvent } from '../functions/analytics/use-analytics';
import { Token } from '../types/auth';
import { DealerGroup, DealerInfo, DealerGroupResponse } from '../types/dealer';
import { DealerService } from '../services';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: {} as Token,
    user: {} as any,
    isAuthenticated: false,
    clarityCookie: "",
    dealers: null as (null | DealerInfo[]),
    selectedDealers: null as (null | DealerInfo[]),
    dealerGroup: null as (DealerGroup | null),
    dealerGroups: null as (DealerGroupResponse[] | null),
    selectedGroup: null as (number | null),
    asmOverride: null as (null | number),
    isAsm: false
  }),
  getters: {

    userName: (state) => {
      return state.user.accessType === 1 ?
        state.user.name :
        state.user.firstName + " " + state.user.surname
    },
    isYopmail: (state) => {
      return state.user.email && state.user.email.indexOf("yopmail") > -1 ? true : false
    },
    email: (state) => {
      return state.user.email as string
    },
    dealerName: (state) => {
      return state.selectedDealers ?
        state.selectedDealers.length === 1 ? state.selectedDealers[0].name : "Multiple Selected" :
        state.user.accessType === 1 ?
          "CarFinance247" :
          state.user.dealerName
    },
    dealerId: (state) => {
      if (state.user.accessType === 1) { // asm no dealer selected yet
        return -1
      }
      if (state.user.accessType === 7) { // dealer user
        return state.user.dealerId
      }
      return -1
    },
    userId: (state) => {
      if (state.user && state.user.accessType === 1) {
        return Number("1" + state.user.userId)
      }
      if (state.user && state.user.accessType === 7) {
        return Number("7" + state.user.dealerContactId)
      }
      return null
    },
    affiliateUserId: (state) => {
      return state.user.accessType === 1 ?
        state.selectedDealers && state.selectedDealers.length === 1 ? state.selectedDealers[0].affiliateUserId : null :
        state.user.affiliateUserId
    },
    affiliateUserIds: (state) => {
      if (state.selectedDealers && state.selectedDealers.length > 0) {
        // Map selectedDealers to extract affiliateUserId, filtering out null values
        return state.selectedDealers.map(dealer => dealer.affiliateUserId).filter(id => id !== null && id !== undefined);
      } else {
        return [];
      }
    },
    isSuperUser: (state) => {
      return state.user && state.user.roles && state.user.roles.find((r: string) => r === "SuperUser") ? true : false;
    },
    canChangeDealer: (state) => {
      return (state.user.accessType === 1 || state.user && state.user.roles && state.user.roles.find((r: string) => r === "SuperUser") ? true : false) && state.dealers && state.dealers.length > 0 ? true : false
    },
    isIntranetUser: (state) => {
      return state.user.accessType === 1 ? true : false;
    },
    isDealer: (state) => {
      return state.user.accessType === 7 ? true : false;
    },

  },
  actions: {
    updateAsmOverride(asmOverride: number | null) {
      this.asmOverride = asmOverride;
    },
    getDealers(asmOverride?: number): Promise<DealerInfo[]> {
      return new Promise(async (resolve, reject) => {

        const tasks: Promise<any>[] = [];
        let dealers: DealerInfo[] = [];
        let groups: DealerGroupResponse[] = [];

        tasks.push(
          new Promise((resolve, reject) => {
            if (asmOverride) {
              DealerService.getDealersByUserId(asmOverride ?? this.user.userId).then(res => {
                if(res.length === 0){
                  DealerService.getDealers().then(res => {
                    dealers = res;
                    resolve(true);
                  }).catch(error => {
                    resolve(false);
                  });
                }
                else{
                  dealers = res;
                }
                
                resolve(true);
              }).catch(error => {
                console.log("no dealers");
                resolve(false);
              });
            }
            else {
              DealerService.getDealers().then(res => {
                dealers = res;
                resolve(true);
              }).catch(error => {
                resolve(false);
              });
            }
          })
        )
        tasks.push(
          new Promise((resolve, reject) => {
            DealerService.getAllGroups().then(res => {
              groups = res ?? [];
              resolve(true);
            }).catch(error => {
              resolve(false);
            });
          })
        )

        await Promise.all(tasks);

        if (this.user.skills.find((s: number) => s === 74)) {
          this.setDealers(dealers);

          if(asmOverride){
            let gs = groups.filter(g => g.dealers.find(d => dealers!.find(asmd => asmd.affiliateUserId === d.affiliateUserId)));
            this.setDealerGroups(gs);
          }
          else{
            this.setDealerGroups(groups);
          }
          
          resolve(dealers);
        }
        else{
          this.setDealers([]);
          resolve([]);
        }
      })
    },
    getDealerGroups() {
      return new Promise(async (resolve, reject) => {
        const groups = await DealerService.getAllGroups();
        this.setDealerGroups(groups);
        resolve(groups);
      })
    },
    setDealers(dealers: DealerInfo[] | null) {
      this.dealers = dealers;
    },
    setDealerGroups(groups: DealerGroupResponse[] | null) {
      this.dealerGroups = groups;
    },
    updateSelectedGroup(group: number | null) {
      this.selectedGroup = group;
    },
    selectDealers(dealers: DealerInfo[] | null) {
      this.selectedDealers = dealers;
    },
    updateCustomerToken(token: Token) {
      this.token = token;
    },
    updateIsAuthenticated(isAuthenticated: boolean) {
      this.isAuthenticated = isAuthenticated;
    },
    updateDealerGroup(group: DealerGroup | null) {
      this.dealerGroup = group;
    },
    login(token: Token, email?: string) {
      this.user = jwtDecode(token.authenticationToken);
      email ? this.user.email = email : null;
      token.authenticationTokenExpiredOn = this.user.exp
      this.token = token;
      this.isAuthenticated = true;
    },
    logout(redirect?: boolean) {
      logEvent(EVENTS.LOGOUT);
      this.dealers = null;
      this.selectedDealers = null;
      this.asmOverride = null;
      this.selectedGroup = null;
      this.dealerGroup = null;
      this.dealerGroups = null;
      this.isAsm = false;
      this.user = {};
      this.isAuthenticated = false;
      this.token = {} as Token;
      if (redirect) {
        this.router.push({ name: "Login" }).then(() => {

        });
      }
    }
  },
  persist: true
});
